import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { createRoot } from "react-dom/client";
import {
	listEnvironments,
	removeEnvironment,
	addEnvironment
} from "client/Features/Landing/recentEnvironmentsService";
import {
	Anchor,
	Form,
	FormItem,
	IconButton,
	InputRow,
	TextInput,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	IRootValidation,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import { isNotEmpty, inline } from "client/Validation/rules";
import * as environmentNamePolicy from "client_legacy/ndaw/environmentNamePolicy";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { useUnmountedCancellation } from "client/Hooks";
import { navigateTo } from "client_legacy/ndaw/navigator";
import styles from "./Page.module.less";
import { ContentBlock } from "../PublicPage/ContentBlock";
import { ensureNotNil, setTrackingId } from "client/Misc/utils";

export type LandingPageProps = {
	supportUrl: string;
};

export const LandingPage = (props: LandingPageProps): ReactElement => {
	const {
		supportUrl
	} = props;

	const [environmentNames, setEnvironmentNames] = useState<readonly string[]>(
		() => listEnvironments()
	);

	const [formData, setFormData] = useState<FormData>({
		name: ""
	});

	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const navigateHandler = useCallback(
		(name: string): void => {
			addEnvironment(name);
			const newPath: string = window.location.protocol + "//" + window.location.host + "/" + name;
			navigateTo(newPath);
		},
		[]
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			const validation: IRootValidation<FormData> = formHandler.validation;

			try {
				const validationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					return;
				}

				const { name } = formData;

				navigateHandler(name);

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[
			formData,
			formHandler.validation,
			globalCancellation,
			navigateHandler
		]
	);

	const deleteHander = useCallback(
		(name: string): void => {
			setEnvironmentNames((x) => x.filter(e => e !== name));
			removeEnvironment(name);
		},
		[]
	);

	return (
		<ContentBlock size="small">
			<h1>{localize("_Welcome")}</h1>

			<div className={styles.container}>
				{environmentNames.length > 0 && (
					<section>
						<span>{localize("_ChooseYourWorkspace")}</span>

						<ul
							className={styles.visitedEnvironmentList}
							{...setTrackingId("visited-environment-list")}
						>
							{environmentNames.map((x): ReactElement => (
								<VisitedEnvironment
									key={x}
									name={x}
									onNavigate={() => navigateHandler(x)}
									onDelete={() => deleteHander(x)}
								/>
							))}
						</ul>

						<span>{localize("_GoToAnotherWorkspace")}</span>
					</section>
				)}

				<Form onSubmit={submitHandler}>
					<FormItem>
						<InputRow>
							<InputRow.Stretch>
								<TextInput
									{...formHandler.for("name").attachToTextInput()}
									placeholder={localize("_YourCompanyName")}
								/>
							</InputRow.Stretch>

							<IconButton
								trackingId="go"
								kind="primary"
								icon="arrow-right"
								onClick={submitHandler}
							/>
						</InputRow>

						<ValidationError {...formHandler.for("name").attachToValidationError()} />
					</FormItem>
				</Form>

				<span className={styles.supportLink}>
					<Anchor
						href={supportUrl}
						target="_blank"
					>
						{localize("_SupportPage")}
					</Anchor>
				</span>
			</div>
		</ContentBlock>
	);
};
LandingPage.displayName = "LandingPage";

type FormData = {
	name: string;
};

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration.forScalar("name")
	.add(
		isNotEmpty(localize("_LandingEnterUrl")),
		inline(
			(value): InputValidationResult => {
				if (value === "") {
					return {
						kind: InputValidationResultKind.Valid
					};
				}

				if (environmentNamePolicy.isValid(value)) {
					return {
						kind: InputValidationResultKind.Valid
					};
				}

				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_LandingInvalidCharacters")
				};
			}
		)
	);

type VisitedEnvironmentProps = {
	name: string;
	onNavigate: () => void;
	onDelete: () => void;
};

const VisitedEnvironment = (props: VisitedEnvironmentProps): ReactElement => {
	const {
		name,
		onNavigate,
		onDelete
	} = props;

	return (
		<li
			{...setTrackingId("visited-environment", `|${name}`)}
			className={styles.visitedEnvironment}
		>
			<div
				className={styles.navigation}
				onClick={onNavigate}
			>
				<Logo />

				<span className={styles.name} title={name}>
					{name}
				</span>
			</div>

			<IconButton
				trackingId="forget"
				kind="secondary"
				icon="trash-can"
				onClick={onDelete}
				attachedOn="left"
			/>
		</li>
	);
};
VisitedEnvironment.displayName = "VisitedEnvironment";

const Logo = (): ReactElement => {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 92 70.35" className={styles.logo}>
			<g>
				<rect width="43.29" height="70.35" rx="4.33" />
				<rect x="48.71" width="43.29" height="46" rx="4.33" />
				<rect x="48.71" y="51.41" width="18.94" height="18.94" rx="4.33" />
				<rect x="73.06" y="51.41" width="18.94" height="18.94" rx="4.33" />
			</g>
		</svg>
	);
};
Logo.displayName = "Logo";

export function init(props: LandingPageProps): void {
	createRoot(
		ensureNotNil(
			document.getElementById("landing-page")
		)
	)
		.render(
			<LandingPage {...props} />
		);
}
